import React, { PureComponent } from 'react'
import { setTitle } from 'ducks/title/utils'
import MatrixEnvironment from 'ducks/chat/MatrixEnvironment'
import { ensureUserIsMember } from 'ducks/chat/utils/rooms'
import { getMatrixClient } from 'ducks/chat/utils/client'
import Loading from 'core/subapps/Loading'
import MatrixUiEnvironment from './lib/MatrixUiEnvironment'

// pages
import ChatPage from './pages/ChatPage'
import SocialPage from './pages/SocialPage'
import ErrorPage from './pages/ChatPage/ErrorPage'
import SocialErrorPage from './pages/SocialPage/ErrorPage'

// routes
const routesMap = {
  ChatPage: {
    component: ChatPage,
    error: ErrorPage,
  },
  SocialPage: {
    component: SocialPage,
    error: SocialErrorPage,
  },
}
let isOnBeforeFileSendMessageHandlerRegistered = false
let isMemberOfAccountRoom = false
export default class ChatSubappRouter extends PureComponent {
  constructor(props) {
    super(props)
    this.roomQueue = {}
    this.totalEventsReceived = 0
  }

  componentDidMount() {
    const { bootstrapChat } = this.props
    bootstrapChat()
    this.initComponent()
  }

  componentDidUpdate() {
    const { isChatEnabled } = this.props
    this.initComponent()
    if (!isChatEnabled) return
    this.updateTitle()
  }

  componentWillUnmount() {
    if (!this.isInitialized) return
    this.isUnmounting = true
    this.stopListeningForRoomUpdates()
    this.updateTitle()
    this.isInitialized = false
  }

  onRoomTimeline = (event, room) => {
    if (this.isUnmounting) return
    if (event.getType() === 'm.room.member' && event.getContent().membership)
      return

    const { onRoomsUpdated } = this.props
    if (onRoomsUpdated && room) {
      this.totalEventsReceived += 1
      if (!this.roomQueue[room.roomId]) {
        this.roomQueue[room.roomId] = {
          room: null,
          events: [],
        }
      }
      this.roomQueue[room.roomId].room = room
      this.roomQueue[room.roomId].events.push(event)
    }
  }

  async initComponent() {
    const { isChatEnabled, isMatrixReady } = this.props
    if (isChatEnabled && !isMatrixReady) {
      MatrixEnvironment.get()
      MatrixUiEnvironment.get()
    }
    if (isChatEnabled && isMatrixReady && !this.isInitialized) {
      this.isInitialized = true
      this.startListeningForRoomUpdates()
      this.updateTitle()
      this.registerOnBeforeFileSendMessageHandler()
    }
    this.ensureAccountRoomMember()
  }

  updateTitle() {
    const { titleName, titleCount } = this.props
    setTitle(titleName, titleCount)
  }

  // Kevin R (2021-02-16)
  // Events in matrix rooms are very verbose and its not uncommon to have
  // 20 or 30 events sent to the server within a 1 second period. In order
  // to prevent this from overwhelming our redux state we buffer all events
  // for 1 second and then flush that as a single redux evet into our state
  // Additionally we also check if any other updates for a room is currently
  // getting executed before flushing that room. This prevents a rare case
  // where an optimistic update for a room could get lost due to an update
  // that came in from a different client (granted the window for this
  // to happen was less than 100ms)
  flushToRedux = () => {
    const { onRoomsUpdated, updatingMatrixIds } = this.props
    if (onRoomsUpdated && this.totalEventsReceived > 0) {
      const updatedRooms = []
      Object.values(this.roomQueue).forEach(roomUpdate => {
        const roomId = roomUpdate.room.roomId
        if (!updatingMatrixIds.includes(roomUpdate.room.roomId)) {
          updatedRooms.push(roomUpdate)
          this.totalEventsReceived -= roomUpdate.events.length
          delete this.roomQueue[roomId]
        }
      })
      onRoomsUpdated(updatedRooms)
    }
  }

  stopListeningForRoomUpdates() {
    const client = getMatrixClient()
    client.off('Room.timeline', this.onRoomTimeline)
    if (this.intervalHandle) clearInterval(this.intervalHandle)
  }

  startListeningForRoomUpdates() {
    const client = getMatrixClient()
    if (!client) return
    client.on('Room.timeline', this.onRoomTimeline)
    this.intervalHandle = setInterval(this.flushToRedux, 1000)
  }

  registerOnBeforeFileSendMessageHandler() {
    const { accountChatRoomId } = this.props
    if (isOnBeforeFileSendMessageHandlerRegistered) return
    if (accountChatRoomId) {
      MatrixUiEnvironment.registerOnBeforeFileSendMessageHandler({
        accountChatRoomId,
      })
      isOnBeforeFileSendMessageHandlerRegistered = true
    }
  }

  ensureAccountRoomMember() {
    const { accountChatRoomId, isMatrixReady } = this.props
    if (isMatrixReady && accountChatRoomId && !isMemberOfAccountRoom) {
      ensureUserIsMember(accountChatRoomId)
      isMemberOfAccountRoom = true
    }
  }

  render() {
    const {
      componentName,
      props,
      isBootstrapped,
      isChatEnabled,
      isSocialEnabled,
      isForbidden,
      isSocialForbidden,
      canUseLiveChatFeature,
    } = this.props
    const routeConfig = routesMap[componentName]
    const availabilities = {
      ChatPage: isChatEnabled,
      SocialPage: isSocialEnabled,
    }
    const errors = {
      ChatPage: isForbidden || !canUseLiveChatFeature,
      SocialPage: isSocialForbidden,
    }
    if (!isBootstrapped) {
      return <Loading />
    }

    if (!availabilities[componentName]) {
      return 'Feature is currently not enabled'
    }

    const Page = errors[componentName]
      ? routeConfig?.error
      : routeConfig?.component

    if (!Page) return null

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Page key={`subapp-${componentName}`} {...props} />
  }
}
